import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { OtaFooter, formatDate, formatDateTime } from '@ota/design-system';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { BookingStatus } from '@ota/api-client';
import { authStore, fetchBooking, fetchBookingPublic, fetchMediaList, fetchProperty } from '../api';
import { PublicHeader } from '../components/PublicHeader';

const STATUS_COLOR: Record<BookingStatus, 'default' | 'success' | 'error' | 'warning' | 'info'> = {
  PENDING: 'warning',
  CONFIRMED: 'success',
  CANCELLED: 'default',
  COMPLETED: 'info',
  PAYMENT_FAILED: 'error',
};

export function BookingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const bookingId = Number(id);
  const navigate = useNavigate();
  const session = authStore.get();

  // Opened from the "View my bookings" email link (no session) — see
  // MyBookingsPage for the equivalent list-page handling and PublicBookingController
  // on the backend.
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const booking = useQuery({
    queryKey: ['booking', bookingId, token],
    queryFn: () => (token ? fetchBookingPublic(bookingId, token) : fetchBooking(bookingId)),
    enabled: (!!session || !!token) && !Number.isNaN(bookingId),
  });

  const property = useQuery({
    queryKey: ['property', booking.data?.propertyId],
    queryFn: () => fetchProperty(booking.data!.propertyId),
    enabled: !!booking.data?.propertyId,
  });

  // Pull room images for the (first) room category in this booking
  const roomCatId = booking.data?.rooms[0]?.roomCategoryId;
  const roomImages = useQuery({
    queryKey: ['media-list', 'ROOM_CATEGORY', roomCatId],
    queryFn: () => fetchMediaList('ROOM_CATEGORY', roomCatId!),
    enabled: !!roomCatId,
  });

  if (!session && !token) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <PublicHeader />
        <Container sx={{ py: 6 }}>
          <Alert severity="warning">Sign in to view your booking.</Alert>
        </Container>
      </Box>
    );
  }

  if (booking.isLoading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <PublicHeader />
        <Container sx={{ py: 4 }}><CircularProgress /></Container>
      </Box>
    );
  }
  if (!booking.data) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <PublicHeader />
        <Container sx={{ py: 4 }}><Alert severity="error">Booking not found.</Alert></Container>
      </Box>
    );
  }

  const b = booking.data;
  const line = b.rooms[0];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicHeader />

      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 }, flex: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Stack>
            <Typography variant="overline" color="secondary.main">Booking</Typography>
            <Typography variant="h3" sx={{ fontFamily: 'monospace' }}>{b.bookingCode}</Typography>
            <Typography variant="caption" color="text.secondary">
              Booked on {formatDateTime(b.createdAt)}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={b.status} color={STATUS_COLOR[b.status]} sx={{ fontWeight: 600 }} />
            <Chip label={b.paymentStatus} variant="outlined" />
          </Stack>
        </Stack>

        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Stack spacing={1.5}>
                  <Typography variant="h5">{b.propertyName}</Typography>
                  {property.data && (
                    <Stack direction="row" spacing={0.75} alignItems="center" color="text.secondary">
                      <LocationOnIcon fontSize="small" />
                      <Typography variant="body2">
                        {[property.data.addressLine1, property.data.area, property.data.city, property.data.state]
                          .filter(Boolean).join(', ')}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Check-in</Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <EventIcon fontSize="small" color="primary" />
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatDate(b.checkIn)}</Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Check-out</Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <EventIcon fontSize="small" color="primary" />
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatDate(b.checkOut)}</Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Nights</Typography>
                    <Typography variant="body1">{b.nightsCount}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Guests</Typography>
                    <Typography variant="body1">{b.guestsCount}</Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">Rooms</Typography>
                    <Typography variant="body1">{line?.roomsCount ?? 1}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {line && (
              <Card>
                <CardContent>
                  <Typography variant="h5" sx={{ mb: 2 }}>Room booked</Typography>
                  <Stack spacing={1.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Category</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{line.name}</Typography>
                    </Stack>
                    {line.mealPlanCode && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          <Stack direction="row" spacing={0.5} alignItems="center" component="span">
                            <RestaurantIcon fontSize="small" />
                            <span>Meal plan</span>
                          </Stack>
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {line.mealPlanCode}{line.mealPlanName ? ' · ' + line.mealPlanName : ''}
                          {line.mealPlanSupplement && line.mealPlanSupplement > 0 && (
                            <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'secondary.main' }}>
                              (+₹{Number(line.mealPlanSupplement).toLocaleString('en-IN')}/night)
                            </Typography>
                          )}
                        </Typography>
                      </Stack>
                    )}
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Nightly rate</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        ₹{Number(line.nightlyRate).toLocaleString('en-IN')}
                      </Typography>
                    </Stack>
                  </Stack>

                  {/* Room images */}
                  {roomImages.isLoading && (
                    <Stack direction="row" sx={{ mt: 2 }}><CircularProgress size={20} /></Stack>
                  )}
                  {roomImages.data && roomImages.data.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="overline" color="secondary.main">Room photos</Typography>
                      <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                        {roomImages.data.map((img) => (
                          <Grid key={img.id} item xs={6} sm={4}>
                            <Box
                              component="img"
                              src={`/api/media/${img.id}`}
                              alt={`${line.name} photo`}
                              sx={{
                                width: '100%', height: 140,
                                objectFit: 'cover', borderRadius: 2,
                                boxShadow: 1,
                              }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                  {roomImages.data && roomImages.data.length === 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      The hotel hasn't uploaded photos for this room category yet.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            )}
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ position: 'sticky', top: 88 }}>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 2 }}>Payment summary</Typography>
                <Stack spacing={1.5}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Total amount</Typography>
                    <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 700 }}>
                      ₹{Number(b.totalAmount).toLocaleString('en-IN')}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip size="small" label={b.paymentStatus} variant="outlined" />
                  </Stack>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button
                    variant="outlined" fullWidth
                    onClick={() => navigate(
                      token ? `/my-bookings?token=${encodeURIComponent(token)}` : '/my-bookings',
                    )}
                  >
                    All bookings
                  </Button>
                  <Button
                    variant="contained" color="secondary" fullWidth
                    onClick={() => navigate(`/hotels/${b.propertyId}`)}
                  >
                    Property page
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <OtaFooter />
    </Box>
  );
}
